/*
 * Pestaña Importaciones — Semáforo: DO sin levante y con levante,
 * cada uno con tarjetas semáforo, barras apiladas por cliente y tabla.
 */
import { useMemo } from "react";
import { Box, Description, TableBasic, Title } from "codekit";

import Panelbox from "./Panelbox";
import Semcards from "./Semcards";
import Stakbars from "./Stakbars";
import { bucketConLevante, bucketSinLevante, fmtCOP } from "./utils/carthelp";

const IMP = "IMPORTACION";
const FUNNEL_KEYS = ["GERENCIA", "NOMBRE", "DIRECTOR DE CUENTA"];

//Columnas de las tablas de segmento (ids sin espacios: se usan como grid-area)
const segmentColumns = [
  { id: "DONROXXX", title: "DO", sorted: false, orderBy: null, center: false },
  { id: "CLIENTEX", title: "Cliente", sorted: false, orderBy: null, center: false },
  { id: "DIRECTOR", title: "Director de Cuenta", sorted: false, orderBy: null, center: false },
  { id: "DIASXXXX", title: "Días", sorted: false, orderBy: null, center: true },
  { id: "VALORXXX", title: "Valor", sorted: false, orderBy: null, center: false },
];

/**
 * Agrupa filas en rangos de semáforo y por cliente
 * @param {Array} mRows Filas de importación de un segmento
 * @param {Function} bucketFn Clasificador de rango
 * @param {string} sDiasKey Campo de días a evaluar
 * @returns {Object} { groups, byClient }
 */
const buildSegment = (mRows, bucketFn, sDiasKey) => {
  const mGroups = {
    verde: { count: 0, valor: 0, rows: [] },
    amarillo: { count: 0, valor: 0, rows: [] },
    rojo: { count: 0, valor: 0, rows: [] },
  };
  const mByClient = {};
  mRows.forEach((r) => {
    const nD = r[sDiasKey];
    if (nD === null) return;
    const sB = bucketFn(nD);
    mGroups[sB].count++;
    mGroups[sB].valor += r.VALOR;
    mGroups[sB].rows.push(r);
    if (!mByClient[r.NOMBRE]) mByClient[r.NOMBRE] = { verde: 0, amarillo: 0, rojo: 0, total: 0 };
    mByClient[r.NOMBRE][sB] += r.VALOR;
    mByClient[r.NOMBRE].total += r.VALOR;
  });
  const mTop = Object.entries(mByClient)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 10)
    .map(([label, v]) => ({ label, ...v }));
  return { groups: mGroups, byClient: mTop };
};

/**
 * Construye las filas de la tabla de un segmento para TableBasic
 * @param {Array} mRows Filas del segmento
 * @param {string} sDiasKey Campo de días
 * @returns {Array} Filas ordenadas por días desc (máx. 50)
 */
const buildTableRows = (mRows, sDiasKey) =>
  mRows
    .filter((r) => r[sDiasKey] !== null)
    .sort((a, b) => b[sDiasKey] - a[sDiasKey])
    .slice(0, 50)
    .map((mRow, nIndex) => ({
      id: nIndex,
      DONROXXX: mRow.DO,
      CLIENTEX: mRow.NOMBRE,
      DIRECTOR: mRow["DIRECTOR DE CUENTA"],
      DIASXXXX: mRow[sDiasKey],
      VALORXXX: fmtCOP(mRow.VALOR),
    }));

/**
 * Renderiza un bloque de segmento (sin o con levante)
 * @param {Object} props Propiedades del bloque
 */
const Segment = ({ title, hint, chartTitle, segment, tableRows, breadcrumb, onFunnel }) => (
  <>
    <Box mt={2} mb={1}>
      <Title variant="title-1" color="secondary-800">
        {title}
      </Title>
    </Box>
    <Box mb={4}>
      <Description variant="description-3" color="neutral-500">
        {hint}
      </Description>
    </Box>
    <Box mb={4}>
      <Semcards groups={segment.groups} onCardClick={(sK) => onFunnel(segment.groups[sK].rows, FUNNEL_KEYS, [`${breadcrumb} · ${sK}`])} />
    </Box>
    <Panelbox title={chartTitle} hint="De mayor a menor, por rango de días · cifras en millones de COP · click en un cliente para ver el detalle">
      <Stakbars
        data={segment.byClient}
        onItemClick={(m) =>
          onFunnel(
            [...segment.groups.verde.rows, ...segment.groups.amarillo.rows, ...segment.groups.rojo.rows].filter((r) => r.NOMBRE === m.label),
            ["TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"],
            [breadcrumb, m.label],
          )
        }
      />
    </Panelbox>
    <Box mb={6}>
      <TableBasic
        columns={segmentColumns}
        data={tableRows}
        dataLength={tableRows.length}
        hasMore={false}
        fetchData={() => {}}
      />
    </Box>
  </>
);

/**
 * Renderiza la pestaña Importaciones — Semáforo
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Datos de trabajo filtrados
 * @param {Function} props.onFunnel Abre el drill-down (scope, keys, breadcrumb)
 */
const Tabimpor = ({ data, onFunnel }) => {
  const mImp = useMemo(() => data.filter((r) => r["TIPO DE OPERACIÓN"] === IMP), [data]);
  const mSin = useMemo(() => mImp.filter((r) => !r._tieneLevante), [mImp]);
  const mCon = useMemo(() => mImp.filter((r) => r._tieneLevante), [mImp]);
  const mSegSin = useMemo(() => buildSegment(mSin, bucketSinLevante, "_dias"), [mSin]);
  const mSegCon = useMemo(() => buildSegment(mCon, bucketConLevante, "_diasLevante"), [mCon]);
  const mRowsSin = useMemo(() => buildTableRows(mSin, "_dias"), [mSin]);
  const mRowsCon = useMemo(() => buildTableRows(mCon, "_diasLevante"), [mCon]);

  return (
    <>
      <Segment
        title="DO sin levante (aún no inicia el reloj de facturación)"
        hint="Corte: 0-7 días · 8-20 días · +20 días desde creación"
        chartTitle="Clientes con Mayor Exposición — Sin Levante"
        segment={mSegSin}
        tableRows={mRowsSin}
        breadcrumb="Sin levante"
        onFunnel={onFunnel}
      />
      <Segment
        title="DO con levante ya emitido (ventana de gracia ~3 días para facturar)"
        hint="Corte: 0-3 días · 4-7 días · +7 días desde el levante"
        chartTitle="Clientes con Mayor Exposición — Con Levante"
        segment={mSegCon}
        tableRows={mRowsCon}
        breadcrumb="Con levante"
        onFunnel={onFunnel}
      />
    </>
  );
};

export default Tabimpor;
